/**
 * SEO component that queries for data with
 *  Gatsby's useStaticQuery React hook
 *
 * See: https://www.gatsbyjs.org/docs/use-static-query/
 */
import { graphql, useStaticQuery } from 'gatsby'
import PropTypes from 'prop-types'
import React from 'react'
import Helmet from 'react-helmet'
import { getWindow } from '../state/utils/utilities-dom'
import { scriptCookies } from '../utils/scriptCookies'
import { scriptBlueConic } from '../utils/scriptBlueConic'

function SEO(props) {
	const { site, allContentfulTenaPaises } = useStaticQuery(
		graphql`
			query {
				site {
					siteMetadata {
						title
						description
						author
						siteUrl
						locale
					}
				}
				allContentfulTenaPaises(
					filter: {
						node_locale: { eq: "es-CO" }
						codigoDeDosLetras: { ne: "N/A" }
					}
				) {
					nodes {
						nombreDelPais
						codigoDeDosLetras
						codigoDelPais
						prefijoTelefonico
						node_locale
						lenguaje
						dominio
					}
				}
			}
		`
	)

	const description = props.description
	const lang = props.lang || site.siteMetadata.locale
	const meta = props.meta
	const title = props.title
	const alternatesInvalidCountries = props.alternatesInvalidCountries || []
	const metaDescription = description || site.siteMetadata.description
	const author = site.siteMetadata.author
	const slug = props.slug || getWindow()?.location?.pathname || '/'
	const siteUrl = site.siteMetadata.siteUrl
	const ogImageUrl = props.image || siteUrl + '/logo.png'
	const script = props.script

	// Organization Schema
	const org = {
		'@context': 'https://schema.org',
		'@type': 'Organization',
		name: 'TENA',
		alternateName: author,
		url: siteUrl,
		logo: ogImageUrl,
		contactPoint: {
			'@type': 'ContactPoint',
			telephone: '+57018000524848',
			contactType: 'customer service',
			contactOption: 'TollFree',
			areaServed: 'CO',
			availableLanguage: 'es',
		},
		sameAs: 'https://www.facebook.com/TenaBienEstar',
	}

	// WebSite Schema
	const website = {
		'@context': 'https://schema.org',
		'@type': 'WebSite',
		'@id': siteUrl + '#website',
		headline: 'TENA',
		name: 'TENA',
		description: site.siteMetadata.description,
		url: siteUrl,
		potentialAction: {
			'@type': 'SearchAction',
			target: siteUrl + '?s={search_term_string}',
			'query-input': 'required name=search_term_string',
		},
	}

	let schemas = []
	if (script) {
		schemas = [org, website, script]
	} else {
		schemas = [org, website]
	}

	const schemaOrgJSONLD = {
		type: 'application/ld+json',
		innerHTML: JSON.stringify(schemas),
	}

	const urlFinal = new URL(siteUrl + slug)

	const hrefLangList = []

	allContentfulTenaPaises.nodes?.forEach(country => {
		const slugWithOutInitialSlash = slug.replace(/^\//, '')
		const finalUrl = new URL(country.dominio + slugWithOutInitialSlash)
		const isInvalidCountry = alternatesInvalidCountries?.find(_country =>
			country.codigoDeDosLetras
				?.toLowerCase()
				?.includes(_country?.toLowerCase())
		)

		if (!isInvalidCountry) {
			hrefLangList.push({
				rel: 'alternate',
				href: finalUrl.href,
				hreflang: `${country.lenguaje}-${country.codigoDeDosLetras}`,
			})
		}
	})

	return (
		<Helmet
			htmlAttributes={{ lang }}
			title={title}
			titleTemplate={`%s`}
			link={([
				{
					rel: 'icon',
					type: 'image/png',
					href: '/favicon.ico',
				},
				{
					rel: 'alternate',
					href: `https://www.tena.com.co${slug}`,
					hreflang: 'es',
				},
				{
					rel: 'alternate',
					href: `https://www.tena.com.co${slug}`,
					hreflang: 'x-default',
				},
			]
			).concat(hrefLangList)}
			meta={[
				{
					property: `fb:app_id`,
					content: `199249840200202`,
				},
				{
					name: `description`,
					content: metaDescription,
				},
				{
					property: `image`,
					content: ogImageUrl,
				},
				{
					property: `og:title`,
					content: title,
				},
				{
					property: `og:description`,
					content: metaDescription,
				},
				{
					property: `og:image`,
					content: ogImageUrl,
				},
				{
					property: `og:image:width`,
					content: `400`,
				},
				{
					property: `og:image:height`,
					content: `300`,
				},
				{
					property: `og:type`,
					content: `website`,
				},
				{
					property: `og:url`,
					content: urlFinal,
				},
				{
					name: `twitter:card`,
					content: `summary`,
				},
				{
					name: `twitter:creator`,
					content: site.siteMetadata.author,
				},
				{
					name: `twitter:title`,
					content: title,
				},
				{
					name: `twitter:description`,
					content: metaDescription,
				},
				{
					name: `twitter:image`,
					content: ogImageUrl,
				},
				{
					name: 'google-site-verification',
					content: process.env.GOOGLE_SITE_VERIFICATION,
				},
				{
					name: 'facebook-domain-verification',
					content: '2dwk4v8zjyj7vauw7atw6rqwjf6xzr',
				},
				props.isNoIndex ?
				{
					name: 'robots',
					content: 'noindex',
				}: '',
			].concat(meta)}
			script={
				(
					scriptCookies[process.env.LOCALE]
					? scriptCookies[process.env.LOCALE].concat([schemaOrgJSONLD])
					: scriptCookies.es.concat([schemaOrgJSONLD])
				)
				.concat(scriptBlueConic[process.env.LOCALE]
						? scriptBlueConic[process.env.LOCALE]
						: scriptBlueConic.es
					)
			}
		>
			{ process.env.LOCALE === 'es-CO' && (<link rel="preconnect" href="https://dev.visualwebsiteoptimizer.com" />)}
			{ process.env.LOCALE === 'es-CO' && (<script type='text/javascript' id='vwoCode'>
				{`
				window._vwo_code=window._vwo_code || function() {
					var account_id=738894,
					version = 1.5,
					settings_tolerance=2000,
					library_tolerance=2500,
					use_existing_jquery=false,
					is_spa=1,
					hide_element='body',
					hide_element_style = 'opacity:0 !important;filter:alpha(opacity=0) !important;background:none !important',
					/* DO NOT EDIT BELOW THIS LINE */
					f=false,d=document,vwoCodeEl=d.querySelector('#vwoCode'),code={use_existing_jquery:function(){return use_existing_jquery},library_tolerance:function(){return library_tolerance},hide_element_style:function(){return'{'+hide_element_style+'}'},finish:function(){if(!f){f=true;var e=d.getElementById('_vis_opt_path_hides');if(e)e.parentNode.removeChild(e)}},finished:function(){return f},load:function(e){var t=d.createElement('script');t.fetchPriority='high';t.src=e;t.type='text/javascript';t.onerror=function(){_vwo_code.finish()};d.getElementsByTagName('head')[0].appendChild(t)},getVersion:function(){return version},getMatchedCookies:function(e){var t=[];if(document.cookie){t=document.cookie.match(e)||[]}return t},getCombinationCookie:function(){var e=code.getMatchedCookies(/(?:^|;)\s?(_vis_opt_exp_\d+_combi=[^;$]*)/gi);e=e.map(function(e){try{var t=decodeURIComponent(e);if(!/_vis_opt_exp_\d+_combi=(?:\d+,?)+\s*$/.test(t)){return''}return t}catch(e){return''}});var i=[];e.forEach(function(e){var t=e.match(/([\d,]+)/g);t&&i.push(t.join('-'))});return i.join('|')},init:function(){if(d.URL.indexOf('__vwo_disable__')>-1)return;window.settings_timer=setTimeout(function(){_vwo_code.finish()},settings_tolerance);var e=d.createElement('style'),t=hide_element?hide_element+'{'+hide_element_style+'}':'',i=d.getElementsByTagName('head')[0];e.setAttribute('id','_vis_opt_path_hides');vwoCodeEl&&e.setAttribute('nonce',vwoCodeEl.nonce);e.setAttribute('type','text/css');if(e.styleSheet)e.styleSheet.cssText=t;else e.appendChild(d.createTextNode(t));i.appendChild(e);var n=this.getCombinationCookie();this.load('https://dev.visualwebsiteoptimizer.com/j.php?a='+account_id+'&u='+encodeURIComponent(d.URL)+'&f='+ +is_spa+'&vn='+version+(n?'&c='+n:''));return settings_timer}};window._vwo_settings_timer = code.init();return code;}();
				`}
			</script>)}
		</Helmet>
	)
}

SEO.defaultProps = {
	lang: `es-CO`,
	meta: [],
	description: ``,
}

SEO.propTypes = {
	description: PropTypes.string,
	lang: PropTypes.string,
	meta: PropTypes.arrayOf(PropTypes.object),
	title: PropTypes.string.isRequired,
}

export default SEO
